import clsx from "clsx";
import { ReactNode, useEffect, useState } from "react";
import { Tweet } from "react-twitter-widgets";
import {
  ArrowDropUpSVG,
  PremiumSVG,
  TelegramSVG,
  TwitterSVG,
} from "../../../../../assets";
import { Separator } from "../../../../../components";
import { BACKEND_AI_AGENT_URL } from "../../../../../config";
import { getGatewayScoreColorAndDesc } from "../../utils";
import { InTheEyesOfCommunity } from "./InTheEyesOfCommunity";
import "./SocialScore.scss";

type RowProps = {
  title: string;
  description: ReactNode;
  icon?: ReactNode;
};

interface SocialMetrics {
  mostViewedMentionsIds: string[];
  generalScore: number;
  shortDescription: string;
  sentimentScore: string;
  followerCount: number;
  tweetsLast7Days: number;
  tweetsLast24Hours: number;
  telegramMembers: number;
  communityViews?: {
    communityViewsAboutTokenUtility: string[];
    communityViewsAboutTokenPrice: string[];
    communityViewsAboutProjectSecurity: string[];
    communityViewsAboutProjectEcosystem: string[];
  };
}

const Row = ({ title, description, icon }: RowProps) => {
  return (
    <div className={`flex flex-col gap-1 w-1/2 pr-4`}>
      <div className="text-sm leading-[20px] flex gap-2 items-center">
        {icon} {title}
      </div>
      <div className="font-medium">{description}</div>
    </div>
  );
};

interface Props {
  projectId: string;
}

export const SocialScore = ({ projectId }: Props) => {
  const score = 6.0;
  const { color: gatewayScoreBgColor } = getGatewayScoreColorAndDesc(score);

  const [data, setData] = useState<SocialMetrics | null>(null);
  const [selectedTweetIndex, setSelectedTweetIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  // const [currentTweetIndex, setCurrentTweetIndex] = useState(0);
  // const [tweetsLoading, setTweetsLoading] = useState(true); // State to track tweets loading

  useEffect(() => {
    const fetchTrendingData = async () => {
      try {
        const response = await fetch(
          `${BACKEND_AI_AGENT_URL}/social-metrics?projectId=${projectId}`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
            },
          },
        );
        const data: SocialMetrics = await response.json();
        setData(data);
      } catch (error) {
        console.error("Failed to fetch trending narratives:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchTrendingData();
  }, [projectId]);

  // const handleTweetLoad = () => {
  //   setTweetsLoading(false);
  // };

  if (isLoading) {
    return <div>Loading...</div>;
  }

  if (!data) {
    return <div>No data available</div>;
  }

  // const handleTweetNavigation = (index: number) => {
  //   setCurrentTweetIndex(index);
  //   setTweetsLoading(true);
  // };

  return (
    <div className="w-full">
      <div className="mb-6 leading-[24px]">
        Strong product-market fit demonstrated through significant usage metrics
        and first-mover advantage in Chainlink CCIP integration, supported by
        diverse sector positioning and exchange presence.
      </div>

      <div className="flex flex-col gap-3">
        <div className="flex flex-col bg-[#55A6FB1A] p-5 rounded-xl flex-1">
          <div className="text-[16px] font-medium mb-4 flex justify-between items-center gap-2">
            <span>
              Social Metrics & Sentiment
              <span className="ml-2">
                <PremiumSVG />
              </span>
            </span>
            <div
              className={clsx(
                "px-2 py-[6px] rounded-md font-medium",
                gatewayScoreBgColor && `bg-[${gatewayScoreBgColor}]`,
              )}
            >
              {data.generalScore}
            </div>
          </div>

          <div className="flex">
            <Row
              title="Followers"
              description={
                <span className="flex items-center gap-1">
                  {data.followerCount} <ArrowDropUpSVG />
                </span>
              }
              icon={<TwitterSVG className="h-4 fill-white" />}
            />
            <Row
              title="Members"
              description={
                <span className="flex items-center gap-1">
                  {data.telegramMembers} <ArrowDropUpSVG />
                </span>
              }
              icon={<TelegramSVG className="h-4 fill-white" />}
            />
          </div>

          <Separator className="my-3" />

          <div className="flex">
            <Row
              title="Ticker mentions"
              description={
                <>
                  <span>{data.tweetsLast7Days}</span>{" "}
                  <span className="text-xs opacity-50">past week</span>
                </>
              }
            />
            <Row
              title="Ticker mention"
              description={
                <>
                  <span>{data.tweetsLast24Hours}</span>{" "}
                  <span className="text-xs opacity-50">24h</span>
                </>
              }
            />
          </div>

          <Separator className="my-3" />

          <div className="flex">
            <Row
              title="Sentiment of comments about the project on X"
              description={
                <span className="text-sm text-[#4CAF50]">
                  {data.sentimentScore}
                </span>
              }
            />
          </div>

          <Separator className="my-3" />

          <div className="text-sm leading-[20px]">{data.shortDescription}</div>
        </div>

        {data.communityViews && (
          <InTheEyesOfCommunity data={data.communityViews} />
        )}

        <div className="flex flex-col bg-[#55A6FB1A] p-5 rounded-xl gap-6 flex-1">
          <div className="text-[16px] font-medium flex items-center gap-2">
            Significant Mentions
            <span>
              <PremiumSVG />
            </span>
          </div>
          <div className="rounded-xl min-h-[200px] w-full flex flex-col items-center justify-center gap-4 twitter-post-container">
            <div className="flex items-center justify-center gap-3">
              {data.mostViewedMentionsIds?.map((id, index) => (
                <button
                  key={id}
                  aria-label={`Show ${index}. tweet`}
                  onClick={() => {
                    setSelectedTweetIndex(index);
                  }}
                  className={`w-4 min-w-4 max-w-4 h-4 min-h-4 max-h-4 rounded-sm ${index === selectedTweetIndex ? "bg-[rgba(255,255,255,0.85)]" : "bg-[rgba(255,255,255,0.2)]"}`}
                ></button>
              ))}
            </div>
            {/* {tweetsLoading && <div>Loading tweet...</div>} */}
            {data.mostViewedMentionsIds?.map((id, index) => (
              <div
                key={id}
                className={`${index === selectedTweetIndex ? "" : "hidden"}`}
              >
                <Tweet
                  tweetId={id}
                  options={{
                    conversation: "none",
                    cards: "hidden",
                    width: "1000px",
                    height: "200px",
                  }}
                  // onLoad={handleTweetLoad}
                />
              </div>
            ))}
          </div>
          {/* <div className="flex justify-center gap-2 mt-4">
            {data.mostViewedMentionsIds?.map((_, index) => (
              <div
                key={index}
                className={`w-4 h-4 rounded-full cursor-pointer ${
                  index === currentTweetIndex ? "bg-white" : "bg-gray-400"
                }`}
                onClick={() => handleTweetNavigation(index)}
              ></div>
            ))}
          </div> */}
        </div>
      </div>
    </div>
  );
};
